/**
 * Rechnungslegung-Tab der Buchhaltungsseite
 * Live-Rechnungslegung (§1865 BGB), Vorjahres-Bericht, DOCX Export
 */

import { useState } from 'react';
import {
  FileText, Calendar, TrendingUp, TrendingDown,
  Scale, Receipt, Sparkles, FileDown, AlertTriangle, Archive, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { RlAssistent } from './RlAssistent';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export function RechnungslegungTab({ selectedKlient, selectedKlientName }) {
  const [rechnungslegung, setRechnungslegung] = useState(null);
  const [liveRechnungslegung, setLiveRechnungslegung] = useState(null);
  const [gerichtsExporting, setGerichtsExporting] = useState(false);

  const handleGerichtsExport = async () => {
    if (!selectedKlient) { toast.error('Bitte einen Betreuten auswählen'); return; }
    setGerichtsExporting(true);
    try {
      const jahr = new Date().getFullYear() - 1;
      const response = await fetch(`${BACKEND_URL}/api/buchhaltung/gerichts-export/${selectedKlient}?jahr=${jahr}`);
      if (!response.ok) throw new Error('Export fehlgeschlagen');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Gerichts-Export_${selectedKlientName?.nachname || 'Klient'}_${jahr}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Gerichts-Export heruntergeladen');
    } catch { toast.error('Gerichts-Export fehlgeschlagen'); }
    finally { setGerichtsExporting(false); }
  };

  const handleLiveRechnungslegung = async () => {
    if (!selectedKlient) { toast.error('Bitte einen Betreuten auswählen'); return; }
    try {
      const result = await fetch(`${BACKEND_URL}/api/buchhaltung/rechnungslegung/live/${selectedKlient}`).then(r => r.json());
      setLiveRechnungslegung(result);
    } catch { toast.error('Fehler beim Laden der Live-Rechnungslegung'); }
  };

  const handleRechnungslegung = async () => {
    if (!selectedKlient) { toast.error('Bitte einen Betreuten auswählen'); return; }
    const jahr = new Date().getFullYear() - 1;
    try {
      const result = await fetch(`${BACKEND_URL}/api/buchhaltung/rechnungslegung/${selectedKlient}?von=${jahr}-01-01&bis=${jahr}-12-31`).then(r => r.json());
      setRechnungslegung(result);
    } catch { toast.error('Fehler'); }
  };

  const handleExportDocx = async () => {
    if (!selectedKlient) { toast.error('Bitte einen Betreuten auswählen'); return; }
    try {
      const response = await fetch(`${BACKEND_URL}/api/buchhaltung/rechnungslegung/export/${selectedKlient}?typ=jahresbericht`);
      if (!response.ok) throw new Error('Export fehlgeschlagen');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Rechnungslegung_${selectedKlientName?.nachname || 'Export'}.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('DOCX exportiert');
    } catch { toast.error('Export fehlgeschlagen'); }
  };

  return (
    <div className="space-y-4" data-testid="rechnungslegung-tab">
      {/* Rechnungslegungs-Assistent: Kontoauszüge hochladen, Vollständigkeits-
          Checkliste, Anforderungen vorbereiten, Bericht herunterladen — bisher
          nirgends im Frontend eingehängt (verifiziert, keine Referenz in der
          Codebase vor dieser Session). Hier in den bestehenden Rechnungslegung-
          Tab eingehängt statt einer neuen Oberfläche. */}
      <RlAssistent selectedKlient={selectedKlient} selectedKlientName={selectedKlientName} />

      {/* Aktions-Buttons */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={handleLiveRechnungslegung} data-testid="btn-live-rechnungslegung">
          <Sparkles className="w-4 h-4 mr-2" /> Live-Rechnungslegung (aktuell)
        </Button>
        <Button variant="outline" onClick={handleRechnungslegung}>
          <Calendar className="w-4 h-4 mr-2" /> Vorjahres-Bericht
        </Button>
        <Button variant="outline" onClick={handleExportDocx} data-testid="btn-export-docx">
          <FileDown className="w-4 h-4 mr-2" /> Als DOCX exportieren
        </Button>
        <Button variant="outline" onClick={handleGerichtsExport} disabled={gerichtsExporting} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="btn-gerichts-export">
          {gerichtsExporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Archive className="w-4 h-4 mr-2" />}
          {gerichtsExporting ? 'Generiere...' : 'Gerichts-Export (ZIP)'}
        </Button>
      </div>

      {/* Live-Rechnungslegung */}
      {liveRechnungslegung ? (
        <div className="space-y-4">
          <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-600" />
                Live-Rechnungslegung gemäß §1865 BGB
              </CardTitle>
              <CardDescription>
                {liveRechnungslegung.klient?.name} - Zeitraum: {liveRechnungslegung.zeitraum?.von} bis {liveRechnungslegung.zeitraum?.bis}
              </CardDescription>
            </CardHeader>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-slate-500">Anfangsstand</p>
                <p className="text-2xl font-bold">{(liveRechnungslegung.vermoegen?.anfangsstand || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-green-600 flex items-center gap-1"><TrendingUp className="w-4 h-4" /> Einnahmen</p>
                <p className="text-2xl font-bold text-green-700">{(liveRechnungslegung.einnahmen?.summe || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
                <p className="text-xs text-slate-400">{liveRechnungslegung.einnahmen?.anzahl || 0} Buchungen</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-red-600 flex items-center gap-1"><TrendingDown className="w-4 h-4" /> Ausgaben</p>
                <p className="text-2xl font-bold text-red-700">{(liveRechnungslegung.ausgaben?.summe || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
                <p className="text-xs text-slate-400">{liveRechnungslegung.ausgaben?.anzahl || 0} Buchungen</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-50">
              <CardContent className="pt-4">
                <p className="text-sm text-slate-600 font-medium">Endstand</p>
                <p className={`text-2xl font-bold ${(liveRechnungslegung.vermoegen?.endstand || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {(liveRechnungslegung.vermoegen?.endstand || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                </p>
              </CardContent>
            </Card>
          </div>

          {liveRechnungslegung.verbindlichkeiten?.summe > 0 && (
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600 flex items-center gap-1"><Scale className="w-4 h-4" /> Offene Verbindlichkeiten</p>
                    <p className="text-xl font-bold text-amber-700">{liveRechnungslegung.verbindlichkeiten.summe.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
                  </div>
                  <Badge variant="outline">{liveRechnungslegung.verbindlichkeiten.anzahl} Positionen</Badge>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 flex items-center gap-1"><Receipt className="w-4 h-4" /> Beleg-Abdeckung</p>
                  <p className="text-xl font-bold">{liveRechnungslegung.beleg_statistik?.abdeckung_prozent || 0}%</p>
                </div>
                <div className="text-right text-sm text-slate-500">
                  <p>{liveRechnungslegung.beleg_statistik?.mit_beleg || 0} von {liveRechnungslegung.beleg_statistik?.buchungen_gesamt || 0} mit Beleg</p>
                  {liveRechnungslegung.beleg_statistik?.ohne_beleg > 0 && (
                    <p className="text-amber-600">{liveRechnungslegung.beleg_statistik.ohne_beleg} ohne Beleg</p>
                  )}
                </div>
              </div>
              <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 transition-all" style={{ width: `${liveRechnungslegung.beleg_statistik?.abdeckung_prozent || 0}%` }} />
              </div>
            </CardContent>
          </Card>

          {liveRechnungslegung.vermoegen?.konten?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Kontenübersicht</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {liveRechnungslegung.vermoegen.konten.map((k, i) => (
                    <div key={i} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                      <div>
                        <p className="font-medium text-slate-700">{k.bezeichnung}</p>
                        {k.iban && <p className="text-xs text-slate-400 font-mono">{k.iban}</p>}
                      </div>
                      <p className={`font-bold ${k.stand >= 0 ? 'text-green-700' : 'text-red-700'}`}>{k.stand.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <p className="text-xs text-slate-400 text-right">Generiert am: {new Date(liveRechnungslegung.generiert_am).toLocaleString('de-DE')}</p>
        </div>
      ) : rechnungslegung ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-green-600 flex items-center gap-1"><TrendingUp className="w-4 h-4" /> Einnahmen</p>
                <p className="text-2xl font-bold text-green-700">{rechnungslegung.einnahmen.summe.toLocaleString('de-DE')} €</p>
                <p className="text-xs text-slate-400">{rechnungslegung.einnahmen.count} Posten</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-red-600 flex items-center gap-1"><TrendingDown className="w-4 h-4" /> Ausgaben</p>
                <p className="text-2xl font-bold text-red-700">{rechnungslegung.ausgaben.summe.toLocaleString('de-DE')} €</p>
                <p className="text-xs text-slate-400">{rechnungslegung.ausgaben.count} Posten</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-slate-600">Saldo</p>
                <p className={`text-2xl font-bold ${rechnungslegung.saldo >= 0 ? 'text-green-700' : 'text-red-700'}`}>{rechnungslegung.saldo.toLocaleString('de-DE')} €</p>
              </CardContent>
            </Card>
          </div>
          {rechnungslegung.ohne_beleg?.count > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="font-medium text-amber-800 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{rechnungslegung.ohne_beleg.count} Umsätze ohne zugeordneten Beleg</p>
            </div>
          )}
          <p className="text-sm text-slate-500">
            Zeitraum: {rechnungslegung.zeitraum?.von} bis {rechnungslegung.zeitraum?.bis} - {rechnungslegung.umsaetze_gesamt || 0} Umsätze - {rechnungslegung.auszuege_vorhanden || 0} Kontoauszüge
          </p>
        </div>
      ) : (
        <Card>
          <CardContent className="py-8 text-center">
            <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 mb-2">Rechnungslegung generieren</p>
            <p className="text-sm text-slate-400 mb-4">Erstellen Sie eine aktuelle Live-Rechnungslegung oder einen Vorjahres-Bericht</p>
            <div className="flex justify-center gap-2">
              <Button onClick={handleLiveRechnungslegung}><Sparkles className="w-4 h-4 mr-2" /> Live-Rechnungslegung</Button>
              <Button variant="outline" onClick={handleRechnungslegung}><Calendar className="w-4 h-4 mr-2" /> Vorjahr</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
